import i18n from "@/shared/lib/i18n";
import { applyPrrPtPtI18n } from "@/prr";
import { ensureFreshClient } from "@/shared/lib/buildVersion";
import { branding, injectFont, injectTheme } from "./config";
import * as React from "react";
import * as Sentry from "@sentry/react";
import { createRoot } from "react-dom/client";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import App from "./App.tsx";
import "./index.css";

const sentryDSN = import.meta.env.VITE_SENTRY_DSN;
const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT;
const forceEnable = import.meta.env.VITE_SENTRY_FORCE_ENABLE === "true";
const enabled = forceEnable || environment !== "development";
const debug = import.meta.env.VITE_SENTRY_DEBUG === "true";

Sentry.init({
  enabled,
  dsn: sentryDSN,
  debug,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration(),
    Sentry.captureConsoleIntegration({
      levels: ["error"],
    }),
  ],
  environment,
  tracesSampleRate: 0.1,
  profilesSampleRate: 0.1,
  tracePropagationTargets: [
    "localhost",
    /.*\.visagio\.com\/(?!auth)/,
    /.*microsoftonline\.com/,
  ],
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 0,
  shouldCreateSpanForRequest: (url) => {
    if (url.includes("/auth/") || url.includes("keycloak")) {
      return false;
    }

    return true;
  },
});

applyPrrPtPtI18n(i18n);

injectFont();

document.title = branding.tabSuffix
  ? `${branding.appTitle} | ${branding.tabSuffix}`
  : branding.appTitle;

const base = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "") || "";
const faviconPath = branding.faviconPath.startsWith("/")
  ? branding.faviconPath.slice(1)
  : branding.faviconPath;
let faviconHref = `${base}/${faviconPath}`.replace(/\/+/g, "/");

if (import.meta.env.DEV) {
  faviconHref += (faviconHref.includes("?") ? "&" : "?") + "t=" + Date.now();
}

let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

if (!link) {
  link = document.createElement("link");
  link.rel = "icon";
  link.type = "image/png";
  link.id = "favicon-link";
  document.head.appendChild(link);
}

link.href = faviconHref;

injectTheme();

ensureFreshClient().finally(() => {
  createRoot(document.getElementById("root")!).render(<App />);
});
